import { useState } from "react";
import DelegationPermissionBadges from "../../delegation/DelegationPermissionBadges";
import { ActiveDelegationModel } from "../../../interfaces/delegation";
import { formatDanishDateTime, formatDanishLongDate } from "../../../util/date";
import EditDelegationPermissionsModal from "./EditDelegationPermissionsModal";
import RevokeDelegationModal from "./RevokeDelegationModal";

interface DelegationActiveTableProps {
  delegations: ActiveDelegationModel[];
}

const DelegationActiveTable = ({ delegations }: DelegationActiveTableProps) => {
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<ActiveDelegationModel | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const isModalOpen = revokeTarget !== null || editTarget !== null;

  if (delegations.length === 0) {
    return <p className="text-muted mb-0">Ingen aktive fuldmagter.</p>;
  }

  return (
    <>
      {error && !editTarget ? (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      ) : null}
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th scope="col">Fuldmægtig</th>
              <th scope="col">Rettigheder</th>
              <th scope="col">Gyldig fra</th>
              <th scope="col">Gyldig til</th>
              <th scope="col">Modtager</th>
              <th scope="col">Betegnelse</th>
              <th scope="col" className="text-end">
                Handling
              </th>
            </tr>
          </thead>
          <tbody>
            {delegations.map((delegation) => (
              <tr key={delegation.id}>
                <td>{delegation.delegateDisplayName}</td>
                <td>
                  <DelegationPermissionBadges
                    permissions={delegation.permissions}
                  />
                </td>
                <td>{formatDanishDateTime(delegation.validFrom)}</td>
                <td>
                  {delegation.validTo
                    ? formatDanishLongDate(delegation.validTo)
                    : "—"}
                </td>
                <td>{delegation.invitedName || "—"}</td>
                <td>{delegation.invitedLabel || "—"}</td>
                <td className="text-end">
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      type="button"
                      id={`delegation-edit-${delegation.id}`}
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEditTarget(delegation);
                        setError(null);
                      }}
                      disabled={isModalOpen}
                    >
                      Rediger
                    </button>
                    <button
                      type="button"
                      id={`delegation-revoke-${delegation.id}`}
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setRevokeTarget(delegation.id)}
                      disabled={isModalOpen}
                    >
                      Tilbagekald
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RevokeDelegationModal
        delegationId={revokeTarget}
        onDelegationIdChange={setRevokeTarget}
        onError={setError}
      />

      <EditDelegationPermissionsModal
        delegation={editTarget}
        onDelegationChange={setEditTarget}
      />
    </>
  );
};

export default DelegationActiveTable;
